import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { ServiceFormRule, ServiceFormSchema } from "@factory/form/form.rule";
import { useFormDefaultService } from "@factory/form/form-default-service.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateService,
  useQyGetServiceById,
  useQyDeleteService,
} from "@factory/query/service.query";
import { GetResponseServiceModel } from "@factory/model/query-model-service";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useEditService() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { getDefault } = useFormDefaultService();
  const navigate = useNavigate();
  const { mapUpdateService, mapDeleteService } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { serviceId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Service
  const handleGetApiSuccess = (data: GetResponseServiceModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("name", responseData?.name || "");
    setValue("description", responseData?.description || "");
    setValue("amount", responseData?.amount);
    setValue("duration", responseData?.duration);
    setValue("isOpenTime", Boolean(responseData?.is_open_time));
    setValue("upgrade", responseData?.upgrade);
    setValue("isActive", Boolean(responseData?.is_active));
    setValue("isSubscription", Boolean(responseData?.is_subscription));
    setValue("days", responseData?.days);
  };
  const {
    data: serviceResponse,
    isLoading,
    isError: serviceError,
  } = useQyGetServiceById(serviceId || "", !!serviceId, handleGetApiSuccess);
  const serviceData = serviceResponse?.data?.[0];

  // API UPDATE Service
  const handleUpdateApiSuccess = () => {
    showSuccess("Service updated successfully");
  };
  const { mutate: updateService, isLoading: isEditLoading } =
    useQyUpdateService(handleUpdateApiSuccess);

  // API DELETE Service
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Service deleted successfully");
  };
  const { mutate: deleteService } = useQyDeleteService(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<ServiceFormSchema>({
    defaultValues: getDefault(serviceData),
    resolver: zodResolver(ServiceFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: ServiceFormSchema) => {
    if (!serviceData) {
      throw new Error("No service data");
    }

    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapUpdateService(updatedForm, serviceData.code);
    updateService(formData);
  };
  const handleValidateError = (err: FieldErrors<ServiceFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!serviceData) {
      throw new Error("No service data found");
    }

    const form = mapDeleteService(serviceData.code);
    deleteService(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    serviceId,
    serviceData,
    isLoading,
    serviceError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
